import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { Spark } from "@sparklab/types";
import { useAuth } from "../contexts/AuthContext";
import { getThemeStyles, DEFAULT_THEME } from "../themeConfig";
import { encodeSparkId } from "../utils/sparkId";

export default function MySparksPage() {
  const [sparks, setSparks] = useState<Spark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpark, setSelectedSpark] = useState<Spark | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const fetchSparks = async () => {
      try {
        setLoading(true);
        const data = await api.getMySparks();
        setSparks(data);
        setSelectedSpark((prev) => prev ?? data[0] ?? null);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : "获取作品失败";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchSparks();
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleDeleteSpark = async () => {
    if (!selectedSpark) return;
    const confirmed = window.confirm(`确定要删除「${selectedSpark.name}」吗？此操作不可撤销。`);
    if (!confirmed) return;

    try {
      setDeletingId(selectedSpark.id);
      await api.deleteSpark(selectedSpark.id);
      setSparks((prev) => {
        const updatedList = prev.filter((spark) => spark.id !== selectedSpark.id);
        setSelectedSpark(updatedList[0] ?? null);
        return updatedList;
      });
      setActionMessage("作品已删除");
      setActionError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "删除作品失败";
      setActionError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePublishSpark = async () => {
    if (!selectedSpark || selectedSpark.is_public) return;

    try {
      setPublishingId(selectedSpark.id);
      const updated = await api.publishSpark(selectedSpark.id, true);
      setSparks((prev) => prev.map((spark) => (spark.id === updated.id ? updated : spark)));
      setSelectedSpark(updated);
      setActionMessage("作品已公开，快去首页展示吧！");
      setActionError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "更新公开状态失败";
      setActionError(message);
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/")}
            className={`px-4 py-2 rounded-xl border-2 ${t.button} font-bold hover:scale-105 transition`}
          >
            ← 返回首页
          </button>
          <button
            onClick={() => navigate("/create")}
            className={`px-4 py-2 rounded-xl border-2 ${t.button} font-bold hover:scale-105 transition`}
          >
            ✨ 去创作
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate("/profile")}
            className={`px-4 py-2 rounded-xl border-2 ${t.button} font-bold hover:scale-105 transition`}
          >
            个人中心
          </button>
          <button
            onClick={handleLogout}
            className={`px-4 py-2 rounded-xl border-2 ${t.button} font-bold hover:scale-105 transition`}
          >
            退出登录
          </button>
        </div>
      </div>

      <header className="text-center mb-10">
        <h1 className={`text-4xl font-bold tracking-wide drop-shadow-lg ${t.header}`}>📁 我的作品库</h1>
        <p className={`text-lg mt-2 ${t.subheader}`}>收藏你施展的每一次魔法</p>
      </header>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`p-6 rounded-3xl shadow-xl border-4 ${t.card} h-full`}>
          <h2 className={`text-2xl font-bold mb-4 ${t.subheader}`}>作品列表</h2>
          {loading ? (
            <p className="text-white">加载中...</p>
          ) : error ? (
            <p className="text-red-200">{error}</p>
          ) : sparks.length === 0 ? (
            <div className="text-center text-white">
              <p>还没有作品，先去创作一个吧！</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-auto pr-2">
              {sparks.map((spark) => (
                <button
                  key={spark.id}
                  onClick={() => setSelectedSpark(spark)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition hover:scale-[1.01] ${
                    t.listItem
                  } ${selectedSpark?.id === spark.id ? "ring-2 ring-white" : ""}`}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold truncate">{spark.name}</h3>
                    <span className="text-sm opacity-80">
                      {new Date(spark.created_at).toLocaleString("zh-CN", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="text-sm opacity-80 mt-2 max-h-12 overflow-hidden text-ellipsis">
                    {spark.prompt}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`p-6 rounded-3xl shadow-xl border-4 ${t.card}`}>
          <h2 className={`text-2xl font-bold mb-4 ${t.subheader}`}>作品预览</h2>
          <div className="bg-black rounded-[30px] p-2 shadow-2xl">
            <div className={`${t.preview} rounded-[20px] overflow-hidden`} style={{ aspectRatio: "9/16" }}>
              {selectedSpark ? (
                <iframe srcDoc={selectedSpark.html} title={selectedSpark.name} className="w-full h-full border-none" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-center px-4">
                  {loading ? "正在加载作品..." : "选择一件作品查看魔法效果"}
                </div>
              )}
            </div>
          </div>

          {selectedSpark && (
            <div className="mt-4">
              <p className={`text-lg font-bold ${t.subheader}`}>{selectedSpark.name}</p>
              <p className="text-sm opacity-80 mt-1">🪄 {selectedSpark.prompt}</p>
              <p className="text-sm opacity-60 mt-1">
                保存时间：{new Date(selectedSpark.created_at).toLocaleString("zh-CN")}
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-bold mt-3">
                <span>👍 Like：{selectedSpark.likes}</span>
                <span>👎 Unlike：{selectedSpark.unlikes}</span>
                <span>{selectedSpark.is_public ? "🌐 已公开" : "🔒 私有"}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={handlePublishSpark}
                  disabled={selectedSpark.is_public || publishingId === selectedSpark.id}
                  className={`px-4 py-2 rounded-xl border-2 font-bold ${
                    selectedSpark.is_public ? "opacity-60 cursor-not-allowed" : t.button1
                  } hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100`}
                >
                  {selectedSpark.is_public ? "已公开" : publishingId === selectedSpark.id ? "公开中..." : "公开展示"}
                </button>
                <button
                  onClick={handleDeleteSpark}
                  disabled={deletingId === selectedSpark.id}
                  className="px-4 py-2 rounded-xl border-2 font-bold border-red-400 text-red-100 hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100"
                >
                  {deletingId === selectedSpark.id ? "删除中..." : "删除作品"}
                </button>
              </div>
              {actionMessage && <p className="text-green-200 text-sm mt-3">{actionMessage}</p>}
              {actionError && <p className="text-red-200 text-sm mt-1">{actionError}</p>}
              <div className="mt-4 rounded-2xl border-2 border-dashed border-white/30 p-4 text-sm opacity-90">
                想和朋友互动或查看评论？点击下方按钮跳转到详情页。
                <button
                  onClick={() => navigate(`/sparks/${encodeSparkId(selectedSpark.id)}`)}
                  className={`mt-3 w-full py-2 rounded-xl border-2 ${t.button2} font-bold hover:scale-105 transition`}
                >
                  打开作品详情
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


